import { Close as CloseIcon } from '@mui/icons-material/';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Button, Chip, Divider, IconButton, Stack, type Theme, ThemeProvider, Typography } from '@mui/material';
import type { Driver as DriverApi, DriveStep } from 'driver.js';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { _ } from '/@/locale';
import type { WalkthroughMixpanelSection } from '/@/store/tracking/events/walkthrough';
import { useDriver } from './useDriver';

export const renderPopover = (
    opts: { driver: DriverApi },
    title: string,
    description: string,
    theme: Theme,
    stepsLength: number,
    tag?: string,
    /** When set (e.g. cross-route tour), invoked instead of `driver.moveNext()` for Siguiente / Finalizar. */
    customNext?: () => void,
    /** When set (e.g. cross-route tour), invoked instead of `driver.movePrevious()` for Anterior. */
    customPrevious?: () => void,
    /** Called when the user clicks Finalizar on the last step (before `moveNext`). Used for Mixpanel `walkthrough_completed`. */
    onLastStepPrimary?: () => void,
    /** Opens Help Center home (caller should destroy the driver). */
    onHelpCenter?: () => void
) => {
    // Multiple selectors to support different driver.js versions just in case

    const popoverElement =
        document.querySelector('.driver-popover') ||
        document.querySelector('.driverjs-popover') ||
        document.querySelector('[data-driver-popover]');

    if (popoverElement) {
        const previousCustom = popoverElement.querySelector('[data-custom-popover]');
        if (previousCustom) {
            ReactDOM.unmountComponentAtNode(previousCustom);
        }

        (popoverElement as HTMLElement).style.padding = '0';
        const container = document.createElement('div');
        container.setAttribute('data-custom-popover', 'true');
        const currentStep = (opts.driver.getActiveIndex() || 0) + 1;

        ReactDOM.render(
            <ThemeProvider theme={theme}>
                <CustomPopover
                    tag={tag}
                    title={title}
                    description={description}
                    driver={opts.driver}
                    currentStep={currentStep}
                    totalSteps={stepsLength}
                    onNext={() => {
                        if (customNext) {
                            customNext();
                        } else {
                            if (opts.driver.isLastStep() && onLastStepPrimary) {
                                onLastStepPrimary();
                            }
                            opts.driver.moveNext();
                        }
                    }}
                    onPrevious={() => {
                        if (customPrevious) {
                            customPrevious();
                        } else {
                            opts.driver.movePrevious();
                        }
                    }}
                    onClose={() => {
                        opts.driver.destroy();
                    }}
                    onHelpCenter={onHelpCenter}
                />
            </ThemeProvider>,
            container
        );

        // Clean up any existing content
        popoverElement.innerHTML = '';
        popoverElement.appendChild(container);

        // Store cleanup function for later use
        (popoverElement as any).__cleanup = () => ReactDOM.unmountComponentAtNode(container);
    }
};

const CustomPopover = ({
    title,
    description,
    driver,
    currentStep,
    totalSteps,
    onNext,
    onPrevious,
    onClose,
    tag,
    onHelpCenter,
}: {
    title: string;
    description: string;
    driver: DriverApi;
    currentStep: number;
    totalSteps: number;
    onNext: () => void;
    onPrevious: () => void;
    onClose: () => void;
    tag?: string;
    onHelpCenter?: () => void;
}) => {
    return (
        <Stack direction='column' spacing={1} py={2}>
            <Stack direction='column' spacing={0.75} px={2}>
                {tag && (
                    <Chip
                        label={tag}
                        size='small'
                        color='secondary'
                        variant='outlined'
                        sx={{
                            alignSelf: 'flex-start',
                            height: 22,
                            fontSize: (t: Theme) => t.typography.caption.fontSize,
                            fontWeight: 700,
                        }}
                    />
                )}
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {currentStep} / {totalSteps}
                    </Typography>
                    <IconButton size='small' onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Typography variant='subtitle1' color='primary' fontWeight={600} px={2}>
                {title}
            </Typography>
            <Typography variant='subtitle1' color='primary' px={2} pb={onHelpCenter ? 1 : 2}>
                {description}
            </Typography>
            {onHelpCenter && (
                <Stack px={2} pb={2}>
                    <Button
                        variant='text'
                        color='secondary'
                        size='small'
                        startIcon={<HelpOutlineIcon sx={{ fontSize: 18 }} />}
                        onClick={onHelpCenter}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0.5 }}
                    >
                        {_('Ver más en el centro de ayuda')}
                    </Button>
                </Stack>
            )}
            <Divider />
            <Stack direction='row' spacing={2} justifyContent='flex-end' px={2} pt={1}>
                {!driver.isFirstStep() && (
                    <Button variant='text' color='secondary' onClick={onPrevious}>
                        {_('Anterior')}
                    </Button>
                )}
                <Button variant='contained' color='secondary' onClick={onNext}>
                    {driver.isLastStep() ? _('Finalizar') : _('Siguiente')}
                </Button>
            </Stack>
        </Stack>
    );
};

const Driver = ({
    ONBOARDING_STORAGE_KEY,
    DRIVER_STEPS,
    isLoading,
    popoverTag,
    onBeforeAutoStart,
    mixpanelSection,
    showHelpCenterLink,
}: {
    ONBOARDING_STORAGE_KEY: string;
    DRIVER_STEPS: DriveStep[];
    isLoading: boolean;
    popoverTag?: string;
    /** Runs once before the delayed auto-start (e.g. switch tab so step selectors exist). */
    onBeforeAutoStart?: () => void;
    /** Mixpanel `section` for `walkthrough_*` events (auto-started tours). */
    mixpanelSection?: WalkthroughMixpanelSection;
    /** Walkthrough “Help center” shortcut (Mis Productores, Mis Lotes, etc.). */
    showHelpCenterLink?: boolean;
}) => {
    const [driverActive, setDriverActive] = useState(false);
    const {
        driverInitialized,
        completed: onboardingCompleted,
        startDriver,
    } = useDriver({
        storageKey: ONBOARDING_STORAGE_KEY,
        steps: DRIVER_STEPS,
        enabled: !isLoading && DRIVER_STEPS.length > 0,
        popoverTag,
        mixpanelSection,
        showHelpCenterLink,
    });

    const startDriverRef = useRef(startDriver);
    startDriverRef.current = startDriver;

    const onBeforeAutoStartRef = useRef(onBeforeAutoStart);
    onBeforeAutoStartRef.current = onBeforeAutoStart;

    // Start driver after all critical data has loaded
    useEffect(() => {
        if (!driverInitialized || onboardingCompleted || driverActive) return;

        // Wait for all critical queries to finish loading
        if (isLoading || DRIVER_STEPS.length === 0) return;

        onBeforeAutoStartRef.current?.();

        // Scroll to the top of the main app content container
        const appContent = document.querySelector('#app-content');
        if (appContent) {
            (appContent as HTMLElement).scrollTop = 0;
        }
        // Add a small delay to ensure DOM elements are properly positioned after data loads
        const timer = setTimeout(() => {
            if (!onboardingCompleted && !driverActive) {
                setDriverActive(true);
                startDriverRef.current();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [DRIVER_STEPS.length, driverInitialized, isLoading, onboardingCompleted, driverActive]);

    // Prevent driver restart on visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && driverActive) {
                return;
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [driverActive]);
    return null;
};

export default Driver;
